// ResetPasswordPage.jsx
// Página para establecer la nueva contraseña (llega desde el link del email).
// Lee el token de ?token=xxx en la URL y lo valida con el backend.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import './ResetPasswordPage.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// ── Política de contraseña (espejo del backend para feedback en tiempo real) ──
const RULES = [
    { id: 'length', label: 'Mínimo 8 caracteres', test: p => p.length >= 8 },
    { id: 'uppercase', label: 'Al menos una mayúscula (A-Z)', test: p => /[A-Z]/.test(p) },
    { id: 'lowercase', label: 'Al menos una minúscula (a-z)', test: p => /[a-z]/.test(p) },
    { id: 'digit', label: 'Al menos un número (0-9)', test: p => /\d/.test(p) },
    { id: 'special', label: 'Al menos un carácter especial', test: p => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(p) },
];

const getStrength = (password) => {
    if (!password) return 0;
    const passed = RULES.filter(r => r.test(password)).length;
    return Math.round((passed / RULES.length) * 100);
};

const STRENGTH_LABEL = (s) => {
    if (s === 0) return { label: '', color: 'transparent' };
    if (s <= 40) return { label: 'Débil', color: '#f87171' };
    if (s <= 60) return { label: 'Regular', color: '#fbbf24' };
    if (s <= 80) return { label: 'Buena', color: '#34d399' };
    return { label: '🔒 Fuerte', color: '#00ffff' };
};

const AVATAR = {
    idle: '/intro.png',
    writing: '/escribir.png',
    password: '/contraseña.png',
    success: '/listo.png',
    error: '/fallo.png',
    invalid: '/fallo.png',
};

// ── Countdown para el token ───────────────────────────────────────────────────
function useCountdown(seconds) {
    const [remaining, setRemaining] = useState(seconds);
    const [isFinished, setIsFinished] = useState(false);

    useEffect(() => {
        if (seconds <= 0) return;
        setRemaining(seconds);
        setIsFinished(false);
        const id = setInterval(() => {
            setRemaining(p => {
                if (p <= 1) {
                    clearInterval(id);
                    setIsFinished(true);
                    return 0;
                }
                return p - 1;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [seconds]);

    const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
    const ss = String(remaining % 60).padStart(2, '0');
    return { remaining, display: `${mm}:${ss}`, isFinished };
}

function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') || '';

    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
    const [tokenStatus, setTokenStatus] = useState('checking'); // 'checking'|'valid'|'invalid'|'expired'
    const [tokenSeconds, setTokenSeconds] = useState(0);

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [showConf, setShowConf] = useState(false);

    const [avatar, setAvatar] = useState('idle');
    const [phase, setPhase] = useState('form');  // 'form'|'loading'|'success'|'error'
    const [serverError, setServerError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({ password: '', confirm: '' });

    const strength = getStrength(password);
    const strengthInfo = STRENGTH_LABEL(strength);
    const { remaining: tokenRemaining, display: countdownDisplay, isFinished } = useCountdown(tokenSeconds);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        document.body.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    // ── Validar el token al cargar la página ──────────────────────────────────
    const validateToken = useCallback(async () => {
        if (!token) { setTokenStatus('invalid'); return; }
        try {
            const res = await fetch(`${API_BASE_URL}/api/validate-reset-token?token=${encodeURIComponent(token)}`);
            const data = await res.json();
            if (data.valid) {
                setTokenStatus('valid');
                setTokenSeconds(data.remaining_seconds || 0);
            } else {
                setTokenStatus(data.reason === 'expired' ? 'expired' : 'invalid');
            }
        } catch {
            setTokenStatus('invalid');
        }
    }, [token]);

    useEffect(() => { validateToken(); }, [validateToken]);

    // Cuando el token expira durante la sesión
    useEffect(() => {
        if (tokenStatus === 'valid' && isFinished) {
            setTokenStatus('expired');
        }
    }, [isFinished, tokenStatus]);

    // ── Validación local del formulario ───────────────────────────────────────
    const validateForm = () => {
        const errors = { password: '', confirm: '' };
        let valid = true;

        const failedRules = RULES.filter(r => !r.test(password));
        if (failedRules.length > 0) {
            errors.password = failedRules[0].label;
            valid = false;
        }
        if (password !== confirm) {
            errors.confirm = 'Las contraseñas no coinciden.';
            valid = false;
        }
        setFieldErrors(errors);
        return valid;
    };

    // ── Submit ─────────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        setServerError('');
        if (!validateForm()) {
            setAvatar('error');
            setTimeout(() => setAvatar('password'), 1800);
            return;
        }

        setPhase('loading');
        setAvatar('writing');

        try {
            const res = await fetch(`${API_BASE_URL}/api/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token,
                    new_password: password,
                    confirm_password: confirm,
                }),
            });

            const data = await res.json();

            if (res.ok) {
                setPhase('success');
                setAvatar('success');
                // Auto-redirect al login después de 3 segundos
                setTimeout(() => navigate('/login?reset=true'), 3200);
            } else if (res.status === 422 && data.detail?.errors) {
                // Errores de política de contraseña del backend
                setPhase('form');
                setAvatar('error');
                setFieldErrors(prev => ({
                    ...prev,
                    password: data.detail.errors[0] || 'Contraseña no válida.',
                }));
                setTimeout(() => setAvatar('password'), 1800);
            } else {
                // Token inválido / expirado / error genérico
                setPhase('error');
                setAvatar('error');
                setServerError(
                    typeof data.detail === 'string'
                        ? data.detail
                        : 'Error al restablecer la contraseña. Solicita un nuevo enlace.'
                );
            }
        } catch {
            setPhase('error');
            setAvatar('error');
            setServerError('No se pudo conectar al servidor. Inténtalo más tarde.');
        }
    };

    // ── RENDER: Checking ──────────────────────────────────────────────────────
    if (tokenStatus === 'checking') {
        return (
            <div className="rsp-page">
                <div className="rsp-container rsp-container--center">
                    <div className="rsp-checking-spinner" />
                    <p className="rsp-checking-text">Verificando enlace…</p>
                </div>
            </div>
        );
    }

    // ── RENDER: Token inválido o expirado ─────────────────────────────────────
    if (tokenStatus === 'invalid' || tokenStatus === 'expired') {
        const isExpired = tokenStatus === 'expired';
        return (
            <div className="rsp-page">
                <div className="rsp-container">
                    <div className="rsp-invalid-icon">{isExpired ? '⏱️' : '🚫'}</div>
                    <h1 className="rsp-invalid-title">
                        {isExpired ? 'Enlace expirado' : 'Enlace inválido'}
                    </h1>
                    <p className="rsp-invalid-body">
                        {isExpired
                            ? 'El enlace de recuperación venció (validez: 15 minutos). Solicita uno nuevo desde la pantalla de inicio de sesión.'
                            : 'Este enlace no es válido o ya fue utilizado. Solo puedes usarlo una vez.'}
                    </p>
                    <Link to="/forgot-password" className="rsp-new-link-btn">
                        🔑 Solicitar nuevo enlace
                    </Link>
                    <div className="rsp-back-row">
                        <Link to="/login" className="rsp-back-link">← Volver al login</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="rsp-page">
            <div className="rsp-container">

                {/* Avatar */}
                <div className="rsp-avatar-wrap">
                    <img
                        src={AVATAR[avatar]}
                        alt="Avatar"
                        className="rsp-avatar-img"
                        key={avatar}
                    />
                </div>

                {/* Theme toggle */}
                <div className="rsp-theme-row">
                    <button
                        className="rsp-theme-btn"
                        onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
                        aria-label="Cambiar tema"
                    >
                        {theme === 'light' ? '🌙' : '☀️'}
                    </button>
                </div>

                {/* ── FASE: FORMULARIO ── */}
                {(phase === 'form' || phase === 'loading') && (
                    <>
                        <div className="rsp-header">
                            <h1 className="rsp-title">Nueva contraseña</h1>
                            <p className="rsp-subtitle">
                                Elige una contraseña segura para proteger tu cuenta.
                            </p>
                        </div>

                        {/* Countdown del token */}
                        {tokenRemaining > 0 && (
                            <div className={`rsp-countdown ${tokenRemaining < 120 ? 'rsp-countdown--urgent' : ''}`}>
                                <span className="rsp-countdown-icon">⏱️</span>
                                <span>Enlace válido por{' '}
                                    <strong className="rsp-countdown-time">{countdownDisplay}</strong>
                                </span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} noValidate className="rsp-form">

                            {/* ── Campo: Nueva contraseña ── */}
                            <div className="rsp-input-group">
                                <label htmlFor="rsp-password" className="rsp-label">
                                    Nueva contraseña
                                </label>
                                <div className="rsp-input-wrap">
                                    <input
                                        id="rsp-password"
                                        type={showPass ? 'text' : 'password'}
                                        className={`rsp-input ${fieldErrors.password ? 'rsp-input--error' : ''}`}
                                        placeholder="Mínimo 8 caracteres"
                                        value={password}
                                        onChange={e => {
                                            setPassword(e.target.value);
                                            setFieldErrors(p => ({ ...p, password: '' }));
                                        }}
                                        onFocus={() => setAvatar('password')}
                                        onBlur={() => phase === 'form' && setAvatar('idle')}
                                        autoComplete="new-password"
                                        disabled={phase === 'loading'}
                                    />
                                    <button
                                        type="button"
                                        className="rsp-eye-btn"
                                        onClick={() => setShowPass(p => !p)}
                                        aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                    >
                                        {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                                {fieldErrors.password && (
                                    <p className="rsp-error" role="alert">{fieldErrors.password}</p>
                                )}
                            </div>

                            {/* ── Medidor de fortaleza ── */}
                            {password.length > 0 && (
                                <div className="rsp-strength-block">
                                    <div className="rsp-strength-bar-bg">
                                        <div
                                            className="rsp-strength-bar-fill"
                                            style={{
                                                width: `${strength}%`,
                                                backgroundColor: strengthInfo.color,
                                            }}
                                        />
                                    </div>
                                    <span
                                        className="rsp-strength-label"
                                        style={{ color: strengthInfo.color }}
                                    >
                                        {strengthInfo.label}
                                    </span>

                                    {/* Checklist de reglas */}
                                    <ul className="rsp-rules-list">
                                        {RULES.map(rule => {
                                            const ok = rule.test(password);
                                            return (
                                                <li
                                                    key={rule.id}
                                                    className={`rsp-rule ${ok ? 'rsp-rule--ok' : 'rsp-rule--fail'}`}
                                                >
                                                    <span className="rsp-rule-icon">
                                                        {ok ? '✅' : '○'}
                                                    </span>
                                                    {rule.label}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}

                            {/* ── Campo: Confirmar contraseña ── */}
                            <div className="rsp-input-group">
                                <label htmlFor="rsp-confirm" className="rsp-label">
                                    Confirmar contraseña
                                </label>
                                <div className="rsp-input-wrap">
                                    <input
                                        id="rsp-confirm"
                                        type={showConf ? 'text' : 'password'}
                                        className={`rsp-input ${fieldErrors.confirm ? 'rsp-input--error' : (confirm && password === confirm ? 'rsp-input--ok' : '')}`}
                                        placeholder="Repite la contraseña"
                                        value={confirm}
                                        onChange={e => {
                                            setConfirm(e.target.value);
                                            setFieldErrors(p => ({ ...p, confirm: '' }));
                                        }}
                                        onFocus={() => setAvatar('writing')}
                                        onBlur={() => phase === 'form' && setAvatar('idle')}
                                        autoComplete="new-password"
                                        disabled={phase === 'loading'}
                                    />
                                    <button
                                        type="button"
                                        className="rsp-eye-btn"
                                        onClick={() => setShowConf(p => !p)}
                                        aria-label={showConf ? 'Ocultar' : 'Mostrar'}
                                    >
                                        {showConf ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                                {fieldErrors.confirm && (
                                    <p className="rsp-error" role="alert">{fieldErrors.confirm}</p>
                                )}
                                {confirm && password === confirm && !fieldErrors.confirm && (
                                    <p className="rsp-match-ok">✅ Las contraseñas coinciden</p>
                                )}
                            </div>

                            {/* Error del servidor */}
                            {serverError && (
                                <div className="rsp-server-error" role="alert">
                                    ⚠️ {serverError}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="rsp-submit-btn"
                                disabled={phase === 'loading' || strength < 100}
                            >
                                {phase === 'loading' ? (
                                    <span className="rsp-spinner-wrap">
                                        <span className="rsp-spinner" />
                                        Guardando...
                                    </span>
                                ) : (
                                    '🔐 Establecer contraseña'
                                )}
                            </button>

                            {strength < 100 && password.length > 0 && (
                                <p className="rsp-hint">
                                    Completa todos los requisitos para habilitar el botón.
                                </p>
                            )}
                        </form>
                    </>
                )}

                {/* ── FASE: ÉXITO ── */}
                {phase === 'success' && (
                    <div className="rsp-success-block">
                        <div className="rsp-success-icon">🎉</div>
                        <h2 className="rsp-success-title">¡Contraseña actualizada!</h2>
                        <p className="rsp-success-body">
                            Tu nueva contraseña está activa. Todas tus sesiones anteriores
                            han sido cerradas por seguridad. Te redirigiremos al login en un momento.
                        </p>
                        <div className="rsp-redirect-indicator">
                            <span className="rsp-redirect-dot" />
                            <span className="rsp-redirect-dot" style={{ animationDelay: '0.2s' }} />
                            <span className="rsp-redirect-dot" style={{ animationDelay: '0.4s' }} />
                        </div>
                        <Link to="/login" className="rsp-login-now-btn">
                            Ir al login ahora →
                        </Link>
                    </div>
                )}

                {/* ── FASE: ERROR SEVERO (token inválido mid-session) ── */}
                {phase === 'error' && (
                    <div className="rsp-error-block">
                        <div className="rsp-error-icon">😔</div>
                        <h2 className="rsp-error-title">Algo salió mal</h2>
                        <p className="rsp-error-body">{serverError}</p>
                        <Link to="/forgot-password" className="rsp-new-link-btn">
                            🔑 Solicitar nuevo enlace
                        </Link>
                    </div>
                )}

                {/* Back link — siempre visible */}
                <div className="rsp-back-row">
                    <Link to="/login" className="rsp-back-link">← Volver al login</Link>
                </div>
            </div>
        </div>
    );
}

export default ResetPasswordPage;